import { Component, Host, h, Element, Prop } from '@stencil/core';
import randomNumberBetween from '../../utils/randomNumberBetween';
import { observeElementVisibility } from '@aha-app/aha-shared';
import { LoadingRowKind } from './types';

export interface AhaLoadingRow extends HTMLAhaLoadingRowElement {}

/**
 * A component that is used to show dummy "loading" content to the user, typically shown while a `fetch` is running.
 *
 * It is possible to show both multiple rows of loading data as well as multiple columns of loading data, as well as specify the widths of those columns.
 */
@Component({
  tag: 'aha-loading-row',
  styleUrl: 'aha-loading-row.css',
  shadow: true,
})
export class AhaLoadingRow {
  @Element() host!: HTMLAhaLoadingRowElement;

  /** The overall style of the skeleton */
  @Prop({ reflect: true }) kind: LoadingRowKind = 'fields';

  /** The number of rows shown in the skeleton row. */
  @Prop({ reflect: true }) rows: number = 1;

  /** The number of columns shown in the skeleton row. */
  @Prop({ reflect: true }) columns: number = 1;

  /** The width of each column in the skeleton row. This is a string of valid CSS values separated by spaces. Any unspecified column values will automatically distribute the remaining available space between them.
   *
   * __Examples__
   *
   * If a row has 2 columns, you could set the `column-width` value to `5em; 50%` to specify both column widths.
   *
   * If a row has 3 columns, you could set the `column-width` value to <code style="white-space: pre-wrap">5em; ; 20%</code> _(note the extra space in the middle!)_ to specify only the first and third columns, and then have the second column automatically take up the rest of the available space. */
  @Prop({ reflect: true, attribute: 'column-widths' }) columnWidths!: string;

  @Prop({ reflect: true, attribute: 'content-width' }) contentWidth: string | undefined;

  private cleanupVisibilityObserver?: () => void;

  connectedCallback() {
    if (!this.columns) this.columns = 1;
    if (!this.rows) this.rows = 1;
  }

  componentDidLoad() {
    this.cleanupVisibilityObserver = observeElementVisibility(this.host, isVisible => {
      if (isVisible) {
        this.host.classList.add('visible');
      } else {
        this.host.classList.remove('visible');
      }
    });
  }

  disconnectedCallback() {
    this.cleanupVisibilityObserver?.();
  }

  render() {
    const columnWidths = (this.columnWidths || '')
      .trim()
      .split(';')
      .map(n => n.trim());
    return (
      <Host class={`kind-${this.kind}`}>
        {[...Array(this.rows).keys()].map(row => {
          return (
            <div class="row">
              {[...Array(this.columns).keys()].map(column => {
                const width = columnWidths[column];
                const contentWidth = this.contentWidth ?? `${randomNumberBetween(10)}%`;
                return (
                  <div
                    class="column"
                    style={{
                      width: width,
                      flexGrow: width ? '' : '1',
                    }}
                  >
                    <div class="content" style={{ width: contentWidth }} />
                  </div>
                );
              })}
            </div>
          );
        })}
      </Host>
    );
  }
}
